<template>
  <div class="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col" style="height: calc(100vh - 220px); min-height: 560px;">
    <ComponentHeader
      title="Workflows"
      subtitle="Every workflow built for this tenant: its status, schedule, output table and last run."
    >
      <button class="btn-secondary-sm" :disabled="loading" @click="load">
        <font-awesome-icon :icon="['fas', 'arrows-rotate']" :spin="loading" />
        Refresh
      </button>
      <button class="btn-accent-sm ml-2" @click="openInBuilder(null)">
        <font-awesome-icon :icon="['fas', 'plus']" />
        New workflow
      </button>
    </ComponentHeader>

    <div class="flex items-center gap-3 px-4 py-2 border-b border-gray-200 text-xs">
      <input
        v-model.trim="query"
        type="search"
        placeholder="Search by name or output table"
        class="w-64 border border-gray-300 rounded-md px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-dark/60"
      />
      <div class="flex items-center gap-1">
        <button
          v-for="f in statusFilters"
          :key="f.key"
          type="button"
          class="wl-chip"
          :class="{ 'is-on': statusFilter === f.key }"
          @click="statusFilter = f.key"
        >
          {{ f.label }} <span class="text-gray-400 tabular-nums">{{ countFor(f.key) }}</span>
        </button>
      </div>
      <label class="ml-auto flex items-center gap-1.5 text-gray-600">
        <input v-model="showInactive" type="checkbox"> Show inactive
      </label>
    </div>

    <div class="flex flex-1 min-h-0">
      <div class="flex-1 min-w-0 overflow-auto">
        <p v-if="error" class="px-4 py-3 text-xs text-red-dark">{{ error }}</p>
        <table v-else class="w-full text-xs">
          <thead class="sticky top-0 bg-white">
            <tr class="text-left uppercase tracking-wide text-gray-500 border-b border-gray-200">
              <th class="px-4 py-2 font-semibold">Name</th>
              <th class="px-4 py-2 font-semibold">Status</th>
              <th class="px-4 py-2 font-semibold">Cadence</th>
              <th class="px-4 py-2 font-semibold">Output table</th>
              <th class="px-4 py-2 font-semibold text-right">Run order</th>
              <th class="px-4 py-2 font-semibold">Last run</th>
              <th class="px-4 py-2 font-semibold">Owner</th>
              <th class="px-4 py-2 font-semibold">Updated</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading && !workflows.length">
              <td class="px-4 py-3 text-gray-500" colspan="8">Loading…</td>
            </tr>
            <tr
              v-for="wf in visible"
              :key="wf.id"
              class="border-b border-gray-100 cursor-pointer"
              :class="selectedId === wf.id ? 'bg-blue-50/70' : 'hover:bg-gray-50'"
              @click="select(wf)"
            >
              <td class="px-4 py-2 text-gray-900">
                {{ wf.name }}
                <span v-if="!wf.is_active" class="ml-1 text-gray-400">inactive</span>
              </td>
              <td class="px-4 py-2"><span :class="statusClass(wf.status)">{{ statusLabel(wf.status) }}</span></td>
              <td class="px-4 py-2 text-gray-600">{{ cadenceLabel(wf.expected_cadence) }}</td>
              <td class="px-4 py-2 font-mono text-gray-600">{{ wf.output_table || '—' }}</td>
              <td class="px-4 py-2 text-right tabular-nums text-gray-600">{{ wf.post_process_order ?? 0 }}</td>
              <td class="px-4 py-2">
                <span v-if="wf.last_run_status" :class="runClass(wf.last_run_status)">{{ wf.last_run_status }}</span>
                <span v-else class="text-gray-400">never</span>
                <span v-if="wf.last_run_at" class="ml-1 text-gray-500">{{ fmtDate(wf.last_run_at) }}</span>
              </td>
              <td class="px-4 py-2 text-gray-600">
                <span v-if="wf.is_orphaned" class="text-red-dark">no owner</span>
                <span v-else>{{ wf.owner_name || '—' }}</span>
              </td>
              <td class="px-4 py-2 text-gray-500">{{ fmtDate(wf.updated_at) }}</td>
            </tr>
            <tr v-if="!loading && !visible.length">
              <td class="px-4 py-6 text-center text-gray-500" colspan="8">
                {{ workflows.length ? 'No workflows match.' : 'No workflows yet.' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <aside v-if="selected" class="w-96 shrink-0 border-l border-gray-200 overflow-y-auto text-xs">
        <div class="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
          <span class="font-semibold text-gray-900 truncate">{{ selected.name }}</span>
          <span :class="statusClass(selected.status)">{{ statusLabel(selected.status) }}</span>
          <button class="ml-auto text-gray-400 hover:text-gray-700" title="Close" @click="close">
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>

        <div class="px-4 py-3 space-y-3">
          <div class="flex flex-wrap gap-x-3 gap-y-1">
            <button class="wl-act" @click="openInBuilder(selected.id)">Open in builder</button>
            <button v-if="selected.status === 'draft'" class="wl-act" :disabled="busy" @click="act('submit-for-review', 'Submitted for review.')">Submit for review</button>
            <button v-if="selected.status === 'pending_review'" class="wl-act" :disabled="busy" @click="approve">Approve</button>
            <button v-if="selected.status === 'pending_review'" class="wl-act" :disabled="busy" @click="act('reject', 'Sent back to draft.')">Reject</button>
            <button v-if="selected.status === 'approved'" class="wl-act" :disabled="busy" @click="act('materialize', 'Materializing in the background.')">Materialize now</button>
            <button class="wl-act" :disabled="busy" @click="setActive(!selected.is_active)">{{ selected.is_active ? 'Deactivate' : 'Activate' }}</button>
            <button class="wl-act text-red-dark" :disabled="busy" @click="remove">Delete</button>
          </div>

          <div class="space-y-2">
            <div>
              <label class="wl-label" for="wl-name">Name</label>
              <input id="wl-name" v-model.trim="form.name" type="text" class="wl-input" />
            </div>
            <div>
              <label class="wl-label" for="wl-desc">Description</label>
              <textarea id="wl-desc" v-model="form.description" rows="3" class="wl-input" placeholder="What this dataset is and what one row means." />
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="wl-label" for="wl-cadence">Cadence</label>
                <select id="wl-cadence" v-model="form.expected_cadence" class="wl-input">
                  <option v-for="c in cadences" :key="c.key" :value="c.key">{{ c.label }}</option>
                </select>
              </div>
              <div>
                <label class="wl-label" for="wl-order" title="Workflows run low to high. Set higher than any workflow whose output this one reads.">Run order</label>
                <input id="wl-order" v-model.number="form.post_process_order" type="number" min="0" step="1" class="wl-input" />
              </div>
            </div>
            <div>
              <label class="wl-label" for="wl-schema">Output schema</label>
              <input id="wl-schema" v-model.trim="form.output_schema" type="text" placeholder="OUTPUTS" class="wl-input font-mono" />
            </div>
            <p v-if="revertsApproval" class="text-amber-700">
              Changing the name, cadence, run order or output schema sends an approved workflow back to draft.
              It stops materializing until it is approved again. The description can change freely.
            </p>
            <div class="flex items-center gap-2">
              <button class="btn-primary-sm" :disabled="!dirty || busy || !form.name" @click="save">Save</button>
              <button v-if="dirty" class="wl-act" @click="resetForm">Discard</button>
            </div>
          </div>

          <dl class="wl-facts">
            <dt>Output table</dt><dd class="font-mono">{{ selected.output_table || '—' }}</dd>
            <dt>Sources</dt><dd>{{ sourceCount(selected) }} data attribute{{ sourceCount(selected) === 1 ? '' : 's' }}</dd>
            <dt>Sheets</dt><dd>{{ (selected.sheets || []).length }}</dd>
            <dt>Last run</dt>
            <dd>
              <span v-if="selected.last_run_status" :class="runClass(selected.last_run_status)">{{ selected.last_run_status }}</span>
              <span v-else class="text-gray-400">never</span>
              <span v-if="selected.last_run_at"> · {{ fmtTime(selected.last_run_at) }}</span>
            </dd>
            <dt>Owner</dt>
            <dd>
              <span v-if="selected.is_orphaned" class="text-red-dark">No active owner</span>
              <span v-else>{{ selected.owner_name || '—' }}</span>
            </dd>
            <dt>Created</dt><dd>{{ fmtTime(selected.created_at) }}<template v-if="selected.created_by_name"> · {{ selected.created_by_name }}</template></dd>
            <dt>Updated</dt><dd>{{ fmtTime(selected.updated_at) }}</dd>
          </dl>

          <div v-if="selected.last_run_error">
            <div class="wl-label">Last run error</div>
            <pre class="wl-error">{{ selected.last_run_error }}</pre>
          </div>
        </div>
      </aside>
    </div>

    <p v-if="notice" class="px-4 py-2 text-xs border-t border-gray-100" :class="noticeError ? 'text-red-dark' : 'text-gray-600'">{{ notice }}</p>
  </div>
</template>

<script>
import axios from 'axios';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import { dataWorkflowBuilderLocation, dataWorkflowLocation } from '../../../router/data_routes';

const API_BASE = '/api/etl_module/data-workflows/';
const STATUS_LABELS = { draft: 'Draft', pending_review: 'Pending review', approved: 'Approved' };
const CADENCES = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'quarterly', label: 'Quarterly' },
  { key: 'none', label: 'Not scheduled' },
];
const REVERTING_FIELDS = ['name', 'expected_cadence', 'post_process_order', 'output_schema'];
const errorText = (e, fallback) => {
  const d = e?.response?.data;
  return d?.error || d?.detail || (d && typeof d === 'object' ? Object.values(d).flat()[0] : null) || fallback;
};

export default {
  name: 'OverviewEtlWorkflows',
  components: { ComponentHeader },
  data() {
    return {
      workflows: [],
      loading: false,
      error: '',
      query: '',
      statusFilter: 'all',
      showInactive: true,
      form: {},
      busy: false,
      notice: '',
      noticeError: false,
      cadences: CADENCES,
      statusFilters: [
        { key: 'all', label: 'All' },
        { key: 'draft', label: 'Draft' },
        { key: 'pending_review', label: 'Pending review' },
        { key: 'approved', label: 'Approved' },
      ],
    };
  },
  computed: {
    visible() {
      const q = this.query.toLowerCase();
      return this.workflows.filter((wf) => (this.statusFilter === 'all' || wf.status === this.statusFilter)
        && (this.showInactive || wf.is_active)
        && (!q || (wf.name || '').toLowerCase().includes(q) || (wf.output_table || '').toLowerCase().includes(q)));
    },
    // The open workflow lives in the path: /data/workflows/:id.
    selectedId() {
      if (this.$route.name !== 'DataWorkflow') return null;
      const id = Number(this.$route.params.id);
      return Number.isFinite(id) && id > 0 ? id : null;
    },
    selected() {
      return this.workflows.find((wf) => wf.id === this.selectedId) || null;
    },
    changes() {
      const wf = this.selected;
      if (!wf) return {};
      const out = {};
      if (this.form.name !== wf.name) out.name = this.form.name;
      if ((this.form.description || '') !== (wf.description || '')) out.description = this.form.description;
      if (this.form.expected_cadence !== wf.expected_cadence) out.expected_cadence = this.form.expected_cadence;
      if (Number(this.form.post_process_order || 0) !== Number(wf.post_process_order || 0)) {
        out.post_process_order = Number(this.form.post_process_order || 0);
      }
      if ((this.form.output_schema || 'OUTPUTS') !== (wf.output_schema || 'OUTPUTS')) out.output_schema = this.form.output_schema || 'OUTPUTS';
      return out;
    },
    dirty() {
      return Object.keys(this.changes).length > 0;
    },
    revertsApproval() {
      return this.selected && this.selected.status === 'approved'
        && REVERTING_FIELDS.some((k) => k in this.changes);
    },
  },
  watch: {
    selectedId(id) {
      if (id == null) return;
      this.notice = '';
      this.resetForm();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    openInBuilder(id) {
      // No id = start a fresh composition (?new=1 tells the builder to clear its canvas).
      const location = dataWorkflowBuilderLocation(id);
      this.$router.push(id == null ? { ...location, query: { new: '1' } } : location);
    },
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await axios.get(API_BASE);
        this.workflows = Array.isArray(data) ? data : (data.results || []);
        if (this.selected) this.resetForm();
      } catch (e) {
        this.error = errorText(e, 'Could not load workflows.');
      } finally {
        this.loading = false;
      }
    },
    // Opening and closing the sidebar are navigations, so Back/Forward and
    // shared links work.
    select(wf) {
      if (wf.id !== this.selectedId) this.$router.push(dataWorkflowLocation(wf.id));
    },
    close() {
      this.$router.push(dataWorkflowLocation(null));
    },
    resetForm() {
      const wf = this.selected;
      if (!wf) return;
      this.form = {
        name: wf.name || '',
        description: wf.description || '',
        expected_cadence: wf.expected_cadence || 'weekly',
        post_process_order: wf.post_process_order ?? 0,
        output_schema: wf.output_schema || 'OUTPUTS',
      };
    },
    flash(text, isError = false) {
      this.notice = text;
      this.noticeError = isError;
    },
    replace(updated) {
      this.workflows = this.workflows.map((wf) => (wf.id === updated.id ? updated : wf));
      this.resetForm();
    },
    async save() {
      this.busy = true;
      const reverts = this.revertsApproval;
      try {
        const { data } = await axios.patch(`${API_BASE}${this.selected.id}/`, this.changes);
        this.replace(data);
        this.flash(reverts ? 'Saved. The workflow is back in draft.' : 'Saved.');
      } catch (e) {
        this.flash(errorText(e, 'Save failed.'), true);
      } finally {
        this.busy = false;
      }
    },
    async act(verb, success) {
      this.busy = true;
      try {
        const { data } = await axios.post(`${API_BASE}${this.selected.id}/${verb}/`);
        if (data && data.id) this.replace(data); else await this.load();
        this.flash(success);
      } catch (e) {
        this.flash(errorText(e, `Could not ${verb.replace(/-/g, ' ')}.`), true);
      } finally {
        this.busy = false;
      }
    },
    approve() {
      const scheduled = this.selected.expected_cadence !== 'none';
      const text = scheduled
        ? 'Approving freezes this workflow\'s SQL and schedules it on its cadence. Approve?'
        : 'Approving freezes this workflow\'s SQL. It is not scheduled; materialize it by hand. Approve?';
      if (window.confirm(text)) this.act('approve', 'Approved.');
    },
    async setActive(isActive) {
      this.busy = true;
      try {
        const { data } = await axios.post(`${API_BASE}${this.selected.id}/set-active/`, { is_active: isActive });
        this.replace(data);
        this.flash(isActive ? 'Activated.' : 'Deactivated. It keeps its definition but no longer runs on schedule.');
      } catch (e) {
        this.flash(errorText(e, 'Could not change active state.'), true);
      } finally {
        this.busy = false;
      }
    },
    async remove() {
      if (!window.confirm('Permanently delete this workflow? This cannot be undone.')) return;
      this.busy = true;
      try {
        await axios.delete(`${API_BASE}${this.selected.id}/`);
        this.workflows = this.workflows.filter((wf) => wf.id !== this.selectedId);
        // The deleted workflow's URL no longer exists; don't leave it in history.
        await this.$router.replace(dataWorkflowLocation(null));
        this.flash('Deleted.');
      } catch (e) {
        this.flash(errorText(e, 'Delete failed.'), true);
      } finally {
        this.busy = false;
      }
    },
    countFor(key) {
      const pool = this.workflows.filter((wf) => this.showInactive || wf.is_active);
      return key === 'all' ? pool.length : pool.filter((wf) => wf.status === key).length;
    },
    sourceCount(wf) {
      return ((wf.studio_config && wf.studio_config.steps) || []).filter((s) => s.da_id || s.workflow_id).length;
    },
    statusLabel(s) { return STATUS_LABELS[s] || s; },
    statusClass(s) {
      if (s === 'approved') return 'text-green-dark font-medium';
      if (s === 'pending_review') return 'text-amber-700 font-medium';
      return 'text-gray-600 font-medium';
    },
    runClass(s) {
      if (s === 'success') return 'text-green-dark';
      if (s === 'failed' || s === 'error') return 'text-red-dark';
      return 'text-gray-600';
    },
    cadenceLabel(c) { return (CADENCES.find((x) => x.key === c) || {}).label || c || '—'; },
    fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString() : ''; },
    fmtTime(iso) { return iso ? new Date(iso).toLocaleString() : '—'; },
  },
};
</script>

<style scoped>
.wl-chip { padding: 2px 8px; border: 1px solid #e5e7eb; border-radius: 999px; color: #4b5563; background: #fff; cursor: pointer; }
.wl-chip.is-on { border-color: #1E1F21; color: #1E1F21; font-weight: 600; }
.wl-act { font-size: 12px; font-weight: 500; color: #4C6E97; background: none; border: 0; padding: 0; cursor: pointer; }
.wl-act:hover { text-decoration: underline; }
.wl-act:disabled { color: #9ca3af; cursor: default; text-decoration: none; }
.wl-act.text-red-dark:not(:disabled) { color: #D53D3D; }
.wl-label { display: block; font-size: 11px; font-weight: 500; color: #6b7280; margin-bottom: 3px; }
.wl-input { width: 100%; border: 1px solid #d1d5db; border-radius: 6px; padding: 4px 8px; font-size: 12px; color: #1f2937; background: #fff; }
.wl-input:focus { outline: none; border-color: #4573D2; box-shadow: 0 0 0 2px rgba(69,115,210,.2); }
.wl-facts { display: grid; grid-template-columns: 88px 1fr; gap: 4px 8px; padding-top: 8px; border-top: 1px solid #f3f4f6; }
.wl-facts dt { color: #6b7280; }
.wl-facts dd { color: #1f2937; margin: 0; word-break: break-all; }
.wl-error { white-space: pre-wrap; font-size: 11px; color: #991b1b; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; padding: 6px 8px; max-height: 200px; overflow: auto; }
</style>
